/** biome-ignore-all lint/suspicious/noGlobalIsNan: no breaking changes */

import { type ChangeEvent, type FocusEvent, useEffect, useState } from 'react';

import { useAtomValue } from 'jotai';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import { aggregationsAtom } from '@core/Atoms/Search/AggregationsAtom';
import type { Filter, FilterKey } from '@core/Entities/Search/SearchFilters/Filter.entity';
import { getFilterFromArray } from '@core/Utils/SearchFilters/GetFilterFromArray';
import { trackFilterEvent } from '@plp/Components/Filters/Filters.tracking';

import { useSkewedScale } from './UseSkewedScale';
import { useUpdateFilters } from './UseUpdateFilters';

export const usePriceRangeFilters = (
  filterName: string,
  range: number[],
  defaultValues: [string | undefined, string | undefined] = [undefined, undefined],
  applyButton?: boolean,
) => {
  const filtersList = useAtomValue(filtersListAtom);
  const { totalCount } = useAtomValue(aggregationsAtom);
  const { updateFilters } = useUpdateFilters();

  const [sliderIndexValue, setSliderIndexValue] = useState({ min: 0, max: 100 });
  const { min, max } = sliderIndexValue;

  const gteId = `${filterName}__gte` as FilterKey;
  const lteId = `${filterName}__lte` as FilterKey;

  const minPrice = defaultValues[0];
  const maxPrice = defaultValues[1];

  // We need a small positive value for our calculations, even if the range starts at 0
  const minValue = range.length > 0 ? Math.max(0.1, range[0]) : 0.1;
  let maxValue = range.length > 0 ? range[range.length - 1] : 1000;

  // Ensure maxValue is always greater than minValue to prevent invalid range errors
  if (maxValue <= minValue) {
    maxValue = minValue + 1;
  }

  // Start with default curve settings
  let steepness = 1;
  let bias = 0.5;

  // Customize the slider feel based on what we're filtering
  if (filterName === 'price') {
    steepness = 8;
    bias = 0.01;
  } else if (filterName === 'monthly-price') {
    steepness = 4;
    bias = 0.01;
  }

  const logSlider = useSkewedScale({
    minPosition: 0,
    maxPosition: 100,
    minValue,
    maxValue,
    steepness,
    bias,
  });

  function calculatePriceBySliderIndex(pos: number) {
    const val = logSlider.value(pos);
    const roundedPrice = calculateRoundedPrice(val);
    const minPriceNum = Number(minPrice) || range[0] || 0;
    const maxPriceNum = Number(maxPrice) || range[range.length - 1] || 0;

    if (roundedPrice <= minPriceNum) return minPriceNum;
    if (roundedPrice >= maxPriceNum) return maxPriceNum;
    return roundedPrice;
  }

  function calculateRoundedPrice(val: number) {
    if (val > 1000) return Math.round(val / 100) * 100;
    else if (val > 500) return Math.round(val / 10) * 10;
    return Math.round(val);
  }

  const [gteValue, setGteValue] = useState(
    (getFilterFromArray(gteId, filtersList)?.value as string | undefined) || minPrice,
  );
  const [lteValue, setLteValue] = useState(
    (getFilterFromArray(lteId, filtersList)?.value as string | undefined) || maxPrice,
  );
  const [localGteValue, setLocalGteValue] = useState(
    (getFilterFromArray(gteId, filtersList)?.value as string | undefined) || minPrice,
  );
  const [localLteValue, setLocalLteValue] = useState(
    (getFilterFromArray(lteId, filtersList)?.value as string | undefined) || maxPrice,
  );

  const handleChange = (changes: [FilterKey, string | number | boolean | undefined][]) => {
    const filterChanges: Filter[] = changes.map(change => {
      return { id: change[0], value: change[1]?.toString() || undefined };
    });

    if (!applyButton) {
      trackFilterEvent(
        filterChanges.length === 1 ? filterChanges[0].id : filterName,
        filterChanges.map(change => change.value) as string[],
        filtersList,
      );

      updateFilters(filterChanges);
    }
  };

  useEffect(() => {
    setGteValue((getFilterFromArray(gteId, filtersList)?.value as string | undefined) || minPrice);
    setLteValue((getFilterFromArray(lteId, filtersList)?.value as string | undefined) || maxPrice);
  }, [filtersList, range.join()]);

  useEffect(() => {
    setLocalGteValue(
      (getFilterFromArray(gteId, filtersList)?.value as string | undefined) || minPrice,
    );
    setLocalLteValue(
      (getFilterFromArray(lteId, filtersList)?.value as string | undefined) || maxPrice,
    );
  }, [filterName]);

  const onGteInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setGteValue(e.target.value);
  };

  const onLteInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setLteValue(e.target.value);
  };

  const onGteInputBlur = (e: FocusEvent<HTMLInputElement, Element>) => {
    handleChange([[gteId, e.target.value]]);
  };

  const onLteInputBlur = (e: FocusEvent<HTMLInputElement, Element>) => {
    handleChange([[lteId, e.target.value]]);
  };

  const canReset = (additionalFilterIds: FilterKey[] = []) => {
    if (applyButton) {
      return localGteValue !== minPrice || localLteValue !== maxPrice;
    } else {
      return (
        filtersList.filter(fltr => {
          const idsToCheck = [...additionalFilterIds, gteId, lteId];
          return idsToCheck.includes(fltr.id);
        }).length > 0
      );
    }
  };

  const reset = (additionalFilterIds: FilterKey[] = []) => {
    const additionalFiltersCleared = additionalFilterIds.map(fltrId => ({
      id: fltrId,
      value: undefined,
    }));

    setSliderIndexValue(prev => ({ ...prev, min: 0, max: 100 }));

    if (applyButton) {
      setLocalGteValue(minPrice || '');
      setLocalLteValue(maxPrice || '');
    } else {
      updateFilters([
        ...additionalFiltersCleared,
        { id: gteId, value: undefined },
        { id: lteId, value: undefined },
      ]);
    }
  };

  const onSliderChange = (values: [number, number] | number) => {
    if (typeof values === 'number') {
      setSliderIndexValue(prev => ({ ...prev, min: values }));
      const price = calculatePriceBySliderIndex(values).toString();
      applyButton ? setLocalGteValue(price) : setGteValue(price);
      return;
    }

    setSliderIndexValue(prev => ({ ...prev, min: values[0], max: values[1] }));
    const minPrice = calculatePriceBySliderIndex(values[0]).toString();
    const maxPrice = calculatePriceBySliderIndex(values[1]).toString();

    if (applyButton) {
      setLocalGteValue(minPrice);
      setLocalLteValue(maxPrice);
    } else {
      setGteValue(minPrice);
      setLteValue(maxPrice);
    }
  };

  const onSliderAfterChange = (values: [number, number]) => {
    if (!applyButton) {
      const minPrice = calculatePriceBySliderIndex(values[0]).toString();
      const maxPrice = calculatePriceBySliderIndex(values[1]).toString();
      handleChange([
        [gteId, minPrice],
        [lteId, maxPrice],
      ]);
    }
  };

  let minVal = applyButton
    ? Number(localGteValue || minPrice || 0)
    : Number(gteValue || minPrice || 0);
  let maxVal = applyButton
    ? Number(localLteValue || maxPrice || range[range.length - 1])
    : Number(lteValue || maxPrice || range[range.length - 1]);

  minVal = isNaN(minVal) || minVal < 0 ? 0 : minVal;
  const logMinVal = minVal === 0 ? 0.1 : minVal;
  maxVal = isNaN(maxVal) || maxVal <= 0 ? range[range.length - 1] || 1000 : maxVal;

  if (maxVal < minVal) {
    maxVal = minVal;
  }

  const selectedRangeIndexes: [number, number] = [
    logSlider.position(logMinVal),
    logSlider.position(maxVal),
  ];

  return {
    handleChange,
    canReset,
    reset,
    totalCount,
    gteInputProps: {
      id: gteId,
      onBlur: applyButton ? undefined : onGteInputBlur,
      onChange: applyButton ? undefined : onGteInputChange,
      value: applyButton ? localGteValue : gteValue,
    },
    lteInputProps: {
      id: lteId,
      onBlur: applyButton ? undefined : onLteInputBlur,
      onChange: applyButton ? undefined : onLteInputChange,
      value: applyButton ? localLteValue : lteValue,
    },
    sliderProps: {
      onChange: onSliderChange,
      onAfterChange: onSliderAfterChange,
      stepCount: 100,
      selectedRangeIndexes,
      min,
      max,
    },
  };
};
